import { Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";

/**
 * Small reusable heart button — saves a tour to the user's wishlist.
 * When the user is anonymous we show a toast (per PM decision).
 */
export default function WishlistButton({ tourId, variant = "floating", className = "" }) {
  const { user, isInWishlist, toggleWishlist } = useAuth();
  const { toast } = useToast();
  const saved = isInWishlist(tourId);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast({
        title: "Please log in to save",
        description: "Sign in with Google to build your wishlist.",
      });
      return;
    }
    try {
      const res = await toggleWishlist(tourId);
      if (res.ok) {
        toast({
          title: res.added ? "Saved to wishlist" : "Removed from wishlist",
        });
      }
    } catch {
      toast({
        title: "Couldn't update wishlist",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const base = "inline-flex items-center justify-center transition-all";
  const styles = variant === "floating"
    ? "w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-[#E5DDCB] shadow hover:scale-110"
    : "gap-2 px-4 h-10 rounded-full border border-[#E5DDCB] bg-white hover:bg-[#F5F0E8] text-sm font-medium text-[#1F4D3A]";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={saved}
      aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
      data-testid={`wishlist-btn-${tourId}`}
      className={`${base} ${styles} ${className}`}
    >
      <Heart
        className={`w-4 h-4 ${saved ? "fill-[#C87A50] text-[#C87A50]" : "text-[#4A3728]"}`}
        strokeWidth={2}
      />
      {variant === "inline" && (
        <span>{saved ? "Saved" : "Save"}</span>
      )}
    </button>
  );
}
